import React, { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

const FAQ: React.FC = () => {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const faqData: FAQItem[] = [
    {
      question: "What is Flow?",
      answer: "Flow is a modern writing platform designed to help writers create, edit, and publish content with ease. It combines powerful editing tools with a clean, distraction-free interface to enhance your writing experience."
    },
    {
      question: "How do I get started with Flow?",
      answer: "Getting started is easy! Simply sign up for a free account, and you can immediately begin writing. You can create your first article using our intuitive editor and publish it to share with others."
    },
    {
      question: "Is Flow free to use?",
      answer: "Flow offers both free and premium plans. The free plan includes basic writing and publishing features, while premium plans offer additional functionality like advanced collaboration tools, custom themes, and priority support."
    },
    {
      question: "Can I collaborate with other writers?",
      answer: "Yes! Flow supports real-time collaboration features that allow you to work with other writers, editors, and reviewers on the same document. You can share drafts, leave comments, and track changes together."
    },
    {
      question: "What file formats does Flow support?",
      answer: "Flow primarily uses Markdown for formatting, which makes your content portable and easy to export. You can also import content from various formats and export your work to HTML, PDF, and other common formats."
    },
    {
      question: "How do I publish my articles?",
      answer: "Once you've finished writing your article, simply click the 'Publish' button in the editor. You can preview your article before publishing and make any final adjustments. Published articles will be available at a unique URL that you can share."
    },
    {
      question: "Can I customize the appearance of my articles?",
      answer: "Yes! Flow offers various customization options including themes, typography settings, and layout preferences. Premium users have access to additional customization features and can even create custom CSS styles."
    },
    {
      question: "How do I edit or update published articles?",
      answer: "You can edit any of your published articles at any time. Simply go to your profile, find the article you want to edit, and click the edit button. Changes will be saved automatically and reflected in the published version."
    },
    {
      question: "Is my content secure on Flow?",
      answer: "Absolutely. We take security seriously and use industry-standard encryption to protect your content. Your drafts are automatically saved and backed up, and we never share your private content without your permission."
    },
    {
      question: "Can I delete my account and content?",
      answer: "Yes, you have full control over your account and content. You can delete individual articles or your entire account at any time. When you delete your account, all associated content will be permanently removed from our servers."
    },
    {
      question: "How do I contact support?",
      answer: "You can reach our support team through the Contact Us page, where you can submit a detailed inquiry. We typically respond within 24 hours during business hours. Premium users also have access to priority support."
    },
    {
      question: "Does Flow work on mobile devices?",
      answer: "Yes! Flow is fully responsive and works great on mobile devices, tablets, and desktop computers. You can write, edit, and publish content from any device with an internet connection."
    }
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Frequently Asked Questions</h1>
        <p className="page-subtitle">Find answers to common questions about Flow</p>
      </div>

      <div className="page-content">
        <section className="content-section">
          <p>
            Can't find the answer you're looking for? Check out our{' '}
            <a href="/contact">Contact Us</a> page to get in touch with our support team.
          </p>
        </section>

        <div className="faq-container">
          {faqData.map((item, index) => (
            <div key={index} className="faq-item">
              <button
                className={`faq-question ${openItems.includes(index) ? 'active' : ''}`}
                onClick={() => toggleItem(index)}
              >
                <span>{item.question}</span>
                <span className="faq-toggle">
                  {openItems.includes(index) ? '−' : '+'}
                </span>
              </button>
              {openItems.includes(index) && (
                <div className="faq-answer">
                  <p>{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <section className="content-section">
          <h2>Still Need Help?</h2>
          <p>
            If you couldn't find the answer to your question, we're here to help!
            Contact our support team and we'll get back to you as soon as possible.
          </p>
          <a href="/contact" className="cta-button">Contact Support</a>
        </section>
      </div>
    </div>
  );
};

export default FAQ;